import React, { useState } from 'react'
import { Save, FileText, Calculator, TrendingUp, AlertCircle, Plus, Minus } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'
import { getProfitLossMetrics, getBalanceSheetMetrics, isBalanceSheetBalanced } from '../../utils/statementUtils'

const FinancialDataEntry = () => {
  const { customer } = useCustomerAuth()
  const [activeTab, setActiveTab] = useState('profit-loss')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  // Products data for Profit & Loss
  const [products, setProducts] = useState([
    {
      product_name: '',
      revenue: '',
      cost_of_goods_sold: '',
      gross_profit: '',
      notes: ''
    }
  ])

  // Operating expenses data
  const [operatingExpenses, setOperatingExpenses] = useState([
    {
      category: '',
      description: '',
      amount: ''
    }
  ])

  // Additional P&L data
  const [plData, setPLData] = useState({
    period_start: '',
    period_end: '',
    other_income: '',
    other_expenses: '',
    net_income: '',
    notes: ''
  })

  // Balance Sheet Data
  const [bsData, setBSData] = useState({
    as_of_date: '',
    // Assets
    cash_and_equivalents: '',
    accounts_receivable: '',
    inventory: '',
    other_current_assets: '',
    total_current_assets: '',
    fixed_assets: '',
    accumulated_depreciation: '',
    net_fixed_assets: '',
    other_assets: '',
    total_assets: '',
    // Liabilities
    accounts_payable: '',
    short_term_debt: '',
    other_current_liabilities: '',
    total_current_liabilities: '',
    long_term_debt: '',
    other_liabilities: '',
    total_liabilities: '',
    // Equity
    owner_equity: '',
    retained_earnings: '',
    total_equity: '',
    notes: ''
  })

  // Product management functions
  const addProduct = () => {
    setProducts([...products, {
      product_name: '',
      revenue: '',
      cost_of_goods_sold: '',
      gross_profit: '',
      notes: ''
    }])
  }

  const removeProduct = (index) => {
    if (products.length > 1) {
      setProducts(products.filter((_, i) => i !== index))
    }
  }

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...products]
    updatedProducts[index][field] = value
    
    // Auto-calculate gross profit
    if (field === 'revenue' || field === 'cost_of_goods_sold') {
      const revenue = parseFloat(updatedProducts[index].revenue) || 0
      const cogs = parseFloat(updatedProducts[index].cost_of_goods_sold) || 0
      updatedProducts[index].gross_profit = (revenue - cogs).toString()
    }
    
    setProducts(updatedProducts)
  }

  // Operating expenses management functions
  const addOperatingExpense = () => {
    setOperatingExpenses([...operatingExpenses, {
      category: '',
      description: '',
      amount: ''
    }])
  }

  const removeOperatingExpense = (index) => {
    if (operatingExpenses.length > 1) {
      setOperatingExpenses(operatingExpenses.filter((_, i) => i !== index))
    }
  }

  const handleOperatingExpenseChange = (index, field, value) => {
    const updatedExpenses = [...operatingExpenses]
    updatedExpenses[index][field] = value
    setOperatingExpenses(updatedExpenses)
  }

  const handlePLChange = (e) => {
    const { name, value } = e.target
    setPLData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleBSChange = (e) => {
    const { name, value } = e.target
    setBSData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const profitLossMetrics = getProfitLossMetrics({
    products,
    operating_expenses: operatingExpenses,
    other_income: plData.other_income,
    other_expenses: plData.other_expenses,
    net_income: plData.net_income
  })

  const bsMetrics = getBalanceSheetMetrics(bsData)

  const validatePLData = () => {
    const required = ['period_start', 'period_end']
    for (let field of required) {
      if (!plData[field]) {
        setError(`Please fill in the ${field.replace('_', ' ')} field`)
        return false
      }
    }
    
    // Validate at least one product with revenue
    const hasValidProduct = products.some(product => 
      product.product_name.trim() && product.revenue
    )
    if (!hasValidProduct) {
      setError('Please add at least one product with a name and revenue')
      return false
    }
    
    return true
  }

  const validateBSData = () => {
    const required = ['as_of_date']
    for (let field of required) {
      if (!bsData[field]) {
        setError(`Please fill in the ${field.replace('_', ' ')} field`)
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      if (activeTab === 'profit-loss') {
        if (!validatePLData()) {
          setLoading(false)
          return
        }

        const financialData = {
          ...plData,
          products: products.filter(product => product.product_name.trim()),
          operating_expenses: operatingExpenses.filter(expense => expense.category.trim() && expense.amount),
          total_revenue: profitLossMetrics.totalRevenue,
          total_cost_of_goods_sold: profitLossMetrics.totalCOGS,
          total_gross_profit: profitLossMetrics.totalGrossProfit,
          total_operating_expenses: profitLossMetrics.totalOperatingExpenses,
          operating_income: profitLossMetrics.totalGrossProfit - profitLossMetrics.totalOperatingExpenses,
          net_income: profitLossMetrics.netIncome
        }

        const response = await apiService.submitFinancialData({
          type: 'profit_loss',
          period: `${plData.period_start}_to_${plData.period_end}`,
          data: financialData
        })
        
        if (response.success) {
          setSuccess('Profit & Loss statement submitted successfully!')
          // Reset form
          setPLData({
            period_start: '',
            period_end: '',
            other_income: '',
            other_expenses: '',
            net_income: '',
            notes: ''
          })
          setProducts([{
            product_name: '',
            revenue: '',
            cost_of_goods_sold: '',
            gross_profit: '',
            notes: ''
          }])
          setOperatingExpenses([{
            category: '',
            description: '',
            amount: ''
          }])
        } else {
          setError(response.message || 'Failed to submit data')
        }
      } else {
        if (!validateBSData()) {
          setLoading(false)
          return
        }
        if (!isBalanceSheetBalanced(bsData)) {
          setError('Balance sheet must balance. Assets must equal Liabilities + Equity within 1 UGX tolerance.')
          setLoading(false)
          return
        }

        const response = await apiService.submitFinancialData({
          type: 'balance_sheet',
          period: bsData.as_of_date,
          data: {
            ...bsData,
            total_current_assets: bsMetrics.currentAssets,
            net_fixed_assets: bsMetrics.netFixedAssets,
            total_assets: bsMetrics.totalAssets,
            total_current_liabilities: bsMetrics.currentLiabilities,
            total_liabilities: bsMetrics.totalLiabilities,
            total_equity: bsMetrics.totalEquity
          }
        })
        
        if (response.success) {
          setSuccess('Balance sheet submitted successfully!')
          setBSData({
            as_of_date: '',
            cash_and_equivalents: '',
            accounts_receivable: '',
            inventory: '',
            other_current_assets: '',
            total_current_assets: '',
            fixed_assets: '',
            accumulated_depreciation: '',
            net_fixed_assets: '',
            other_assets: '',
            total_assets: '',
            accounts_payable: '',
            short_term_debt: '',
            other_current_liabilities: '',
            total_current_liabilities: '',
            long_term_debt: '',
            other_liabilities: '',
            total_liabilities: '',
            owner_equity: '',
            retained_earnings: '',
            total_equity: '',
            notes: ''
          })
        } else {
          setError(response.message || 'Failed to submit data')
        }
      }
    } catch (error) {
      setError('Failed to submit financial data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Financial Data Entry</h1>
          <p className="text-gray-600 mt-2">
            Submit your Profit & Loss and Balance Sheet information
          </p>
        </div>

        {/* Alerts */}
        {success && (
          <div className="mb-6 bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-md flex items-center">
            <TrendingUp className="h-5 w-5 mr-2" />
            {success}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <div className="card">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('profit-loss')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'profit-loss'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Calculator className="h-5 w-5 inline mr-2" />
                Profit & Loss Statement
              </button>
              <button
                onClick={() => setActiveTab('balance-sheet')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'balance-sheet'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="h-5 w-5 inline mr-2" />
                Balance Sheet
              </button>
            </nav>
          </div>

          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {activeTab === 'profit-loss' ? (
                // Profit & Loss Form
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="period_start" className="form-label">
                        Period Start Date *
                      </label>
                      <input
                        type="date"
                        id="period_start"
                        name="period_start"
                        value={plData.period_start}
                        onChange={handlePLChange}
                        className="form-input"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="period_end" className="form-label">
                        Period End Date *
                      </label>
                      <input
                        type="date"
                        id="period_end"
                        name="period_end"
                        value={plData.period_end}
                        onChange={handlePLChange}
                        className="form-input"
                        required
                      />
                    </div>
                  </div>

                  {/* Products Section */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Products Revenue</h3>
                      <button
                        type="button"
                        onClick={addProduct}
                        className="btn btn-sm btn-secondary flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Product
                      </button>
                    </div>
                    
                    {products.map((product, index) => (
                      <div key={index} className="border rounded-lg p-4 mb-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-700">Product {index + 1}</h4>
                          {products.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeProduct(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="md:col-span-2">
                            <label className="form-label">
                              Product Name *
                            </label>
                            <input
                              type="text"
                              value={product.product_name}
                              onChange={(e) => handleProductChange(index, 'product_name', e.target.value)}
                              className="form-input"
                              placeholder="e.g., Maize, Sugarcane, Coffee"
                              required
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Revenue *
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={product.revenue}
                              onChange={(e) => handleProductChange(index, 'revenue', e.target.value)}
                              className="form-input"
                              placeholder="0.00"
                              required
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Cost of Goods Sold
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={product.cost_of_goods_sold}
                              onChange={(e) => handleProductChange(index, 'cost_of_goods_sold', e.target.value)}
                              className="form-input"
                              placeholder="0.00"
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Gross Profit (Auto-calculated)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={product.gross_profit}
                              className="form-input bg-gray-100"
                              readOnly
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Notes
                            </label>
                            <input
                              type="text"
                              value={product.notes}
                              onChange={(e) => handleProductChange(index, 'notes', e.target.value)}
                              className="form-input"
                              placeholder="Additional notes"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Operating Expenses Section */}
                  <div className="border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Operating Expenses</h3>
                      <button
                        type="button"
                        onClick={addOperatingExpense}
                        className="btn btn-sm btn-secondary flex items-center"
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Add Expense
                      </button>
                    </div>
                    
                    {operatingExpenses.map((expense, index) => (
                      <div key={index} className="border rounded-lg p-4 mb-4 bg-gray-50">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-medium text-gray-700">Expense {index + 1}</h4>
                          {operatingExpenses.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeOperatingExpense(index)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="form-label">
                              Category *
                            </label>
                            <input
                              type="text"
                              value={expense.category}
                              onChange={(e) => handleOperatingExpenseChange(index, 'category', e.target.value)}
                              className="form-input"
                              placeholder="e.g., Transport, Labor, Equipment"
                              required
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Description
                            </label>
                            <input
                              type="text"
                              value={expense.description}
                              onChange={(e) => handleOperatingExpenseChange(index, 'description', e.target.value)}
                              className="form-input"
                              placeholder="Brief description"
                            />
                          </div>
                          <div>
                            <label className="form-label">
                              Amount *
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={expense.amount}
                              onChange={(e) => handleOperatingExpenseChange(index, 'amount', e.target.value)}
                              className="form-input"
                              placeholder="0.00"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary Calculations */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                      <div>
                        <label className="form-label">
                          Total Revenue
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={products.reduce((sum, product) => sum + (parseFloat(product.revenue) || 0), 0).toFixed(2)}
                          className="form-input bg-gray-100"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          Total Cost of Goods Sold
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={products.reduce((sum, product) => sum + (parseFloat(product.cost_of_goods_sold) || 0), 0).toFixed(2)}
                          className="form-input bg-gray-100"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          Total Gross Profit
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={(products.reduce((sum, product) => sum + (parseFloat(product.revenue) || 0), 0) - products.reduce((sum, product) => sum + (parseFloat(product.cost_of_goods_sold) || 0), 0)).toFixed(2)}
                          className="form-input bg-gray-100"
                          readOnly
                        />
                      </div>
                      <div>
                        <label className="form-label">
                          Total Operating Expenses
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={operatingExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0).toFixed(2)}
                          className="form-input bg-gray-100"
                          readOnly
                        />
                      </div>
                    </div>
                  </div>

                  {/* Additional P&L Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="other_income" className="form-label">
                        Other Income
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="other_income"
                        name="other_income"
                        value={plData.other_income}
                        onChange={handlePLChange}
                        className="form-input"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label htmlFor="other_expenses" className="form-label">
                        Other Expenses
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="other_expenses"
                        name="other_expenses"
                        value={plData.other_expenses}
                        onChange={handlePLChange}
                        className="form-input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="net_income" className="form-label">
                      Net Income (Auto-calculated)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="net_income"
                      name="net_income"
                      value={profitLossMetrics.netIncome.toFixed(2)}
                      readOnly
                      className="form-input bg-gray-100"
                      placeholder="0.00"
                    />
                  </div>

                  <div>
                    <label htmlFor="notes" className="form-label">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={plData.notes}
                      onChange={handlePLChange}
                      rows={4}
                      className="form-input"
                      placeholder="Any additional notes or explanations..."
                    />
                  </div>
                </div>
              ) : (
                // Balance Sheet Form
                <div className="space-y-8">
                  <div>
                    <label htmlFor="as_of_date" className="form-label">
                      As of Date *
                    </label>
                    <input
                      type="date"
                      id="as_of_date"
                      name="as_of_date"
                      value={bsData.as_of_date}
                      onChange={handleBSChange}
                      className="form-input"
                      required
                    />
                  </div>

                  {/* Assets Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Assets</h3>
                    
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-700">Current Assets</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4">
                        <div>
                          <label htmlFor="cash_and_equivalents" className="form-label">
                            Cash and Equivalents
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="cash_and_equivalents"
                            name="cash_and_equivalents"
                            value={bsData.cash_and_equivalents}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="accounts_receivable" className="form-label">
                            Accounts Receivable
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="accounts_receivable"
                            name="accounts_receivable"
                            value={bsData.accounts_receivable}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="inventory" className="form-label">
                            Inventory
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="inventory"
                            name="inventory"
                            value={bsData.inventory}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="other_current_assets" className="form-label">
                            Other Current Assets
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="other_current_assets"
                            name="other_current_assets"
                            value={bsData.other_current_assets}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="total_current_assets" className="form-label">
                          Total Current Assets
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="total_current_assets"
                          name="total_current_assets"
                          value={bsData.total_current_assets}
                          onChange={handleBSChange}
                          className="form-input"
                          placeholder="0.00"
                        />
                      </div>
                    </div>

                    <div className="space-y-4 mt-6">
                      <h4 className="font-medium text-gray-700">Fixed Assets</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4">
                        <div>
                          <label htmlFor="fixed_assets" className="form-label">
                            Fixed Assets
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="fixed_assets"
                            name="fixed_assets"
                            value={bsData.fixed_assets}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="accumulated_depreciation" className="form-label">
                            Accumulated Depreciation
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="accumulated_depreciation"
                            name="accumulated_depreciation"
                            value={bsData.accumulated_depreciation}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="total_assets" className="form-label">
                        Total Assets
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        id="total_assets"
                        name="total_assets"
                        value={bsMetrics.totalAssets.toFixed(2)}
                        readOnly
                        className="form-input bg-gray-100"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* Liabilities Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Liabilities</h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="accounts_payable" className="form-label">
                            Accounts Payable
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="accounts_payable"
                            name="accounts_payable"
                            value={bsData.accounts_payable}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="short_term_debt" className="form-label">
                            Short-term Debt
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="short_term_debt"
                            name="short_term_debt"
                            value={bsData.short_term_debt}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="long_term_debt" className="form-label">
                            Long-term Debt
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="long_term_debt"
                            name="long_term_debt"
                            value={bsData.long_term_debt}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="other_liabilities" className="form-label">
                            Other Liabilities
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="other_liabilities"
                            name="other_liabilities"
                            value={bsData.other_liabilities}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="total_liabilities" className="form-label">
                          Total Liabilities
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="total_liabilities"
                          name="total_liabilities"
                          value={bsMetrics.totalLiabilities.toFixed(2)}
                          readOnly
                          className="form-input bg-gray-100"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Equity Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Equity</h3>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="owner_equity" className="form-label">
                            Owner's Equity
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="owner_equity"
                            name="owner_equity"
                            value={bsData.owner_equity}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                        <div>
                          <label htmlFor="retained_earnings" className="form-label">
                            Retained Earnings
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            id="retained_earnings"
                            name="retained_earnings"
                            value={bsData.retained_earnings}
                            onChange={handleBSChange}
                            className="form-input"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      <div>
                        <label htmlFor="total_equity" className="form-label">
                          Total Equity
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="total_equity"
                          name="total_equity"
                          value={bsMetrics.totalEquity.toFixed(2)}
                          readOnly
                          className="form-input bg-gray-100"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="notes" className="form-label">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={bsData.notes}
                      onChange={handleBSChange}
                      rows={4}
                      className="form-input"
                      placeholder="Any additional notes or explanations..."
                    />
                  </div>
                </div>
              )}

              <div className={`mt-6 rounded-xl p-4 border ${isBalanceSheetBalanced(bsData) ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'}`}>
                <p className="text-sm font-medium text-gray-700">
                  {isBalanceSheetBalanced(bsData)
                    ? 'Balance check passes: Assets match Liabilities + Equity within 1 UGX.'
                    : `Balance sheet out of balance by ${Math.abs(bsMetrics.balanceDiff).toFixed(2)} UGX. Please review totals.`}
                </p>
              </div>
              <div className="mt-8 flex justify-end space-x-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    if (activeTab === 'profit-loss') {
                      setPLData({
                        period_start: '',
                        period_end: '',
                        other_income: '',
                        other_expenses: '',
                        net_income: '',
                        notes: ''
                      })
                      setProducts([{
                        product_name: '',
                        revenue: '',
                        cost_of_goods_sold: '',
                        gross_profit: '',
                        notes: ''
                      }])
                      setOperatingExpenses([{
                        category: '',
                        description: '',
                        amount: ''
                      }])
                    } else {
                      setBSData({
                        as_of_date: '',
                        cash_and_equivalents: '',
                        accounts_receivable: '',
                        inventory: '',
                        other_current_assets: '',
                        total_current_assets: '',
                        fixed_assets: '',
                        accumulated_depreciation: '',
                        net_fixed_assets: '',
                        other_assets: '',
                        total_assets: '',
                        accounts_payable: '',
                        short_term_debt: '',
                        other_current_liabilities: '',
                        total_current_liabilities: '',
                        long_term_debt: '',
                        other_liabilities: '',
                        total_liabilities: '',
                        owner_equity: '',
                        retained_earnings: '',
                        total_equity: '',
                        notes: ''
                      })
                    }
                  }}
                >
                  Clear Form
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <>
                      <Save className="h-5 w-5 mr-2" />
                      Submit Data
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinancialDataEntry
